'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DigestEvent {
  id: string;
  event_type: 'time_off_approved' | 'gap_resolved' | 'new_message' | 'new_urgent' | 'schedule_change' | string;
  title: string;
  detail: string | null;
  branch: string | null;
  actor_name: string | null;
  related_id: string | null;
  related_table: string | null;
  created_at: string;
}

export function useActivityDigest() {
  const [events, setEvents] = useState<DigestEvent[]>([]);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const supabase = useMemo(() => createClient(), []);

  const fetchLastSeen = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    const { data } = await supabase
      .from('user_last_seen')
      .select('last_seen_at')
      .eq('user_id', user.id)
      .single();
    return data?.last_seen_at ?? null;
  }, [user, supabase]);

  const fetchEvents = useCallback(async (since: string | null) => {
    let query = supabase
      .from('activity_digest_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (since) {
      query = query.gt('created_at', since);
    }

    const { data } = await query;
    setEvents(data ?? []);
    setLoading(false);
  }, [supabase]);

  const markSeen = useCallback(async () => {
    if (!user) return;
    const now = new Date().toISOString();
    await supabase
      .from('user_last_seen')
      .upsert({ user_id: user.id, last_seen_at: now }, { onConflict: 'user_id' });
    setLastSeenAt(now);
  }, [user, supabase]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const since = await fetchLastSeen();
      setLastSeenAt(since);
      await fetchEvents(since);
    })();
  }, [user, fetchLastSeen, fetchEvents]);

  return { events, lastSeenAt, loading, markSeen };
}
